/** Which sprite and frame draws each element (docs/spec/04 §3). */

import { E, EXPLODE_STAGES } from '../sim/elements';
import type { CaveState } from '../sim/types';
import type { SpriteName } from './sprites';
import { hash } from './sprites/types';

export interface CellLook {
  sprite: SpriteName | null;
  frame: number;
}

const NONE: CellLook = { sprite: null, frame: 0 };

/** Digby standing about: mostly still, now and then a blink or a fit of foot-tapping. */
export function digbyLook(s: Pick<CaveState, 'digby' | 'scan'>, anim: number): CellLook {
  const d = s.digby;
  if (d.moving || d.pushing) return { sprite: d.facing < 0 ? 'digbyLeft' : 'digbyRight', frame: Math.floor(anim / 2) };
  if (d.idle < 4) return { sprite: 'digbyStand', frame: 0 };
  const block = Math.floor(anim / 32);
  const mood = hash(block, 0, 99) % 8;
  const within = anim % 32;
  if (mood < 3 && within < 4) return { sprite: 'digbyBlink', frame: 0 };
  if (mood >= 6) return { sprite: Math.floor(within / 3) % 2 === 0 ? 'digbyTap' : 'digbyStand', frame: 0 };
  return { sprite: 'digbyStand', frame: 0 };
}

/** The look of `el`; `anim` is the 25 Hz animation clock. */
export function cellLook(el: number, s: CaveState, anim: number): CellLook {
  switch (el) {
    case E.SPACE: return NONE;
    case E.DIRT: return { sprite: 'dirt', frame: 0 };
    case E.BRICK: return { sprite: 'brick', frame: 0 };
    case E.MAGIC_WALL:
      return s.magic.phase === 'active' ? { sprite: 'magicWall', frame: Math.floor(anim / 2) } : { sprite: 'brick', frame: 0 };
    case E.STEEL:
    case E.OUTBOX_CLOSED: return { sprite: 'steel', frame: 0 };
    case E.INBOX:
    case E.OUTBOX_OPEN: return { sprite: Math.floor(anim / 6) % 2 === 0 ? 'door' : 'steel', frame: 0 };
    case E.BOULDER:
    case E.BOULDER_FALLING: return { sprite: 'boulder', frame: 0 };
    case E.DIAMOND:
    case E.DIAMOND_FALLING: return { sprite: 'diamond', frame: Math.floor(anim / 2) };
    case E.DIGBY: return digbyLook(s, anim);
    case E.AMOEBA: return { sprite: 'amoeba', frame: Math.floor(anim / 3) };
    case E.EXPANDING_WALL: return { sprite: 'expandingWall', frame: 0 };
    case E.SLIME: return { sprite: 'slime', frame: Math.floor(anim / 3) };
    case E.DOOR_OPENING: return { sprite: 'doorOpening', frame: 0 };
    case E.CLOCK: return { sprite: 'clock', frame: Math.floor(anim / 10) };
    default:
  }
  if (el >= E.FIREFLY && el < E.FIREFLY + 4) return { sprite: 'firefly', frame: Math.floor(anim / 2) };
  if (el >= E.BUTTERFLY && el < E.BUTTERFLY + 4) return { sprite: 'butterfly', frame: Math.floor(anim / 2) };
  if (el >= E.EXPLODE && el < E.EXPLODE + EXPLODE_STAGES) return { sprite: 'explosion', frame: el - E.EXPLODE };
  if (el >= E.DIAMOND_BIRTH && el < E.DIAMOND_BIRTH + EXPLODE_STAGES) return { sprite: 'explosion', frame: el - E.DIAMOND_BIRTH };
  if (el >= E.BIRTH && el < E.BIRTH + 3) return { sprite: 'explosion', frame: 2 - (el - E.BIRTH) };
  if (el >= E.KEY && el < E.KEY + 3) return { sprite: (['key0', 'key1', 'key2'] as const)[el - E.KEY] ?? 'key0', frame: 0 };
  if (el >= E.DOOR && el < E.DOOR + 3) return { sprite: (['door0', 'door1', 'door2'] as const)[el - E.DOOR] ?? 'door0', frame: 0 };
  return NONE;
}
